// src/components/TherapistConnection.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon'; // Assuming you have an Icon component for SVG icons

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function TherapistConnection({ token, userRole }) {
  const [therapist, setTherapist] = useState(null);
  const [invitationCode, setInvitationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (userRole === 'patient') {
      loadTherapistInfo();
    }
  }, [userRole]);

  const loadTherapistInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/invitations/my-therapist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.has_therapist) {
        setTherapist(response.data.therapist);
      }
    } catch (error) {
      console.error('Error loading therapist info:', error);
    }
  };

  const useInvitationCode = async () => {
    if (!invitationCode.trim()) {
      setError('Ange en inbjudningskod');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${API_BASE}/api/invitations/use-invitation`,
        { invitation_code: invitationCode.toUpperCase() },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess(response.data.message);
      setTherapist(response.data.therapist);
      setInvitationCode('');
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunde inte använda inbjudningskod');
    } finally {
      setLoading(false);
    }
  };

  if (userRole !== 'patient') {
    return null; // This component is only for patients
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">Min Terapeut</h3>
      
      {therapist ? (
        <div className="space-y-4">
          <div className="bg-green-100 text-green-800 p-4 rounded mb-5 border border-green-300 flex items-center gap-2">
            <Icon name="check" size={16} />
            Du är ansluten till en terapeut!
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">{therapist.name}</h4>
            <div className="space-y-2">
              {therapist.email && (
                <p className="text-gray-600 flex items-center gap-2">
                  <Icon name="mail" size={16} />
                  <strong>Email:</strong> {therapist.email}
                </p>
              )}
              {therapist.phone && (
                <p className="text-gray-600 flex items-center gap-2">
                  <Icon name="phone" size={16} />
                  <strong>Telefon:</strong> {therapist.phone}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-gray-600 mb-6">Du har ingen terapeut ansluten än.</p>
          
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900">Anslut till en terapeut</h4>
            <p className="text-gray-600">Be din terapeut om en inbjudningskod och ange den här:</p>
            
            <div className="flex gap-3">
              <input
                type="text"
                value={invitationCode}
                onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                placeholder="Ange inbjudningskod (t.ex. ABC12345)"
                maxLength="8"
                style={{ textTransform: 'uppercase', letterSpacing: '2px' }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button 
                onClick={useInvitationCode}
                disabled={loading || !invitationCode.trim()}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? 'Ansluter...' : 'Anslut'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {error && <div className="bg-red-100 text-red-800 p-3 rounded border border-red-300 text-sm">{error}</div>}
      {success && <div className="bg-green-100 text-green-800 p-4 rounded mb-5 border border-green-300">{success}</div>}
    </div>
  );
}

// src/components/PatientInvitations.js - For therapists
function PatientInvitations({ token, userRole }) {
  const [invitations, setInvitations] = useState([]);
  const [patients, setPatients] = useState([]);
  const [newInvitation, setNewInvitation] = useState({
    patient_email: '',
    personal_message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (userRole === 'therapist') {
      loadInvitations();
      loadPatients();
    }
  }, [userRole]);

  const loadInvitations = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/invitations/my-invitations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setInvitations(response.data);
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  };

  const loadPatients = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/invitations/my-patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPatients(response.data.patients);
    } catch (error) {
      console.error('Error loading patients:', error);
    }
  };

  const createInvitation = async () => {
    if (!newInvitation.patient_email) {
      setError('Ange patientens e-post');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${API_BASE}/api/invitations/create-invitation`,
        newInvitation,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess(`Inbjudningskod skapad: ${response.data.invitation_code}`);
      setNewInvitation({ patient_email: '', personal_message: '' });
      loadInvitations();
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunde inte skapa inbjudning');
    } finally {
      setLoading(false);
    }
  };

  if (userRole !== 'therapist') {
    return null; // This component is only for therapists
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Mina Klienter</h3>
      
      {/* Current Patients */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Anslutna Klienter ({patients.length})</h4>
        {patients.length === 0 ? (
          <p className="text-gray-600">Inga klienter anslutna än.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {patients.map(patient => (
              <div key={patient.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Icon name="user" size={16} className="text-emerald-600" />
                  {patient.name}
                </h5>
                <p className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                  <Icon name="mail" size={14} />
                  <strong>Email:</strong> {patient.email}
                </p>
                {patient.phone && (
                  <p className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                    <Icon name="phone" size={14} />
                    <strong>Telefon:</strong> {patient.phone}
                  </p>
                )}
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <Icon name="calendar" size={14} />
                  <strong>Ansluten:</strong> {new Date(patient.assigned_date).toLocaleDateString('sv-SE')}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create New Invitation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Skapa Ny Inbjudning</h4>
        
        <div className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Klientens e-post:</label>
            <input
              type="email"
              value={newInvitation.patient_email}
              onChange={(e) => setNewInvitation({
                ...newInvitation,
                patient_email: e.target.value
              })}
              placeholder="patient@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Personligt meddelande (valfritt):</label>
            <textarea
              value={newInvitation.personal_message}
              onChange={(e) => setNewInvitation({
                ...newInvitation,
                personal_message: e.target.value
              })}
              placeholder="Skriv ett personligt meddelande till din klient som kommer visas i inbjudningsemailet..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
            />
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
            <div className="flex items-center">
              <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-blue-800">
                <strong>Info:</strong> Inbjudningen gäller i 7 dagar från skapande. Efter det måste en ny inbjudan skapas.
              </p>
            </div>
          </div>
          
          <button 
            onClick={createInvitation}
            disabled={loading || !newInvitation.patient_email}
            className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {loading ? 'Skapar...' : 'Skapa Inbjudning'}
          </button>
        </div>
      </div>

      {/* Invitation History */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Inbjudningshistorik</h4>
        {invitations.length === 0 ? (
          <p className="text-gray-600">Inga inbjudningar skapade än.</p>
        ) : (
          <div className="space-y-4">
            {invitations.map(invitation => (
              <div key={invitation.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <strong className="text-gray-900 flex items-center gap-2">
                    <Icon name="key" size={14} />
                    Kod: {invitation.invitation_code}
                  </strong>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                    invitation.status === 'active' ? 'bg-green-100 text-green-800' :
                    invitation.status === 'used' ? 'bg-blue-100 text-blue-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {invitation.status === 'active' && <Icon name="check" size={12} />}
                    {invitation.status === 'used' && <Icon name="check" size={12} />}
                    {invitation.status === 'expired' && <Icon name="x" size={12} />}
                    {invitation.status === 'active' && 'Aktiv'}
                    {invitation.status === 'used' && 'Använd'}
                    {invitation.status === 'expired' && 'Utgången'}
                  </span>
                </div>
                
                <p className="text-sm text-gray-600 mb-1"><strong>För:</strong> {invitation.patient_email}</p>
                <p className="text-sm text-gray-600 mb-1"><strong>Skapad:</strong> {new Date(invitation.created_at).toLocaleDateString('sv-SE')}</p>
                <p className="text-sm text-gray-600 mb-1"><strong>Utgår:</strong> {new Date(invitation.expires_at).toLocaleDateString('sv-SE')}</p>
                
                {invitation.status === 'used' && invitation.patient_name && (
                  <p className="text-sm text-gray-600"><strong>Använd av:</strong> {invitation.patient_name}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {error && <div className="bg-red-100 text-red-800 p-3 rounded border border-red-300 text-sm">{error}</div>}
      {success && <div className="bg-green-100 text-green-800 p-4 rounded mb-5 border border-green-300">{success}</div>}
    </div>
  );
}

export { TherapistConnection, PatientInvitations };